/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { messages } from '../../common/messages';

type DisclaimerAcknowledgementProps = {
	isSaving: boolean;
	onAgree: () => void;
	onBack: () => void;
	onCancel?: () => void;
};

const disclaimerStyles = css({
	paddingTop: token('space.050'),
	paddingRight: token('space.0'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.0'),
});

export const DisclaimerAcknowledgement = ({
	isSaving,
	onAgree,
	onBack,
	onCancel,
}: DisclaimerAcknowledgementProps): JSX.Element => {
	const intl = useIntl();

	return (
		<div data-testid="agent-managed-extension-disclaimer-step">
			<Stack space="space.150">
				<div
					role="group"
					aria-labelledby="agent-managed-extension-disclaimer-title"
					aria-describedby="agent-managed-extension-disclaimer-copy"
				>
					<Stack space="space.100">
						<Text as="strong" weight="semibold" id="agent-managed-extension-disclaimer-title">
							{intl.formatMessage(messages.disclaimerTitle)}
						</Text>
						<div css={disclaimerStyles} id="agent-managed-extension-disclaimer-copy">
							<Text>{intl.formatMessage(messages.disclaimerCopy)}</Text>
						</div>
					</Stack>
				</div>
				<Inline space="space.100" alignInline="end">
					{onCancel && (
						<Button appearance="subtle" onClick={onCancel} isDisabled={isSaving}>
							{intl.formatMessage(messages.cancel)}
						</Button>
					)}
					<Button appearance="subtle" onClick={onBack} isDisabled={isSaving}>
						{intl.formatMessage(messages.disclaimerBack)}
					</Button>
					<Button
						appearance="primary"
						testId="agent-managed-extension-disclaimer-agree"
						onClick={onAgree}
						isDisabled={isSaving}
						autoFocus
						iconAfter={isSaving ? () => <Spinner size="small" /> : undefined}
					>
						{intl.formatMessage(messages.disclaimerAgree)}
					</Button>
				</Inline>
			</Stack>
		</div>
	);
};
